import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminTokenControls from './AdminTokenControls';
import { apiFetch, getErrorMessage, parseJsonSafely, toUserErrorMessage } from './api';
import ConfirmDialog from './ConfirmDialog';
import { useGlobalErrorEffect } from './globalErrors';
import {
  HelpTooltip,
  SectionHeader,
  SettingsActions,
  SettingsFeedback,
  SettingsHeader,
  SettingsHint,
  buildSettingsTabs,
  emptyEmployeeForm,
} from './adminUI';
import EmployeeModal from './admin/EmployeeModal';
import StepModal from './admin/StepModal';
import UpdatesOverview from './admin/UpdatesOverview';
import { formatDateTimeDisplay } from './dateTime';
import { useRoleConfig } from './RoleConfigContext';
import { buildOrderStageLegendConfig } from './orderStageLegend';
import { ROLE_COLUMN_ACCESS_OPTIONS, normalizeAllowedColumns } from './roleColumnAccess';
import useEscapeKey from './useEscapeKey';
import { Button, Modal, ModalHeader } from './ui';

const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/i;
const EMPLOYEE_HIDDEN_COLUMN_KEYS = new Set(['orderNumber', 'customer']);

function normalizeEmployeeIds(value) {
  return Array.isArray(value)
    ? Array.from(new Set(value.map((item) => String(item || '').trim()).filter(Boolean)))
    : [];
}

function buildAppSettingsState(source = {}, prev = {}) {
  return {
    publicBaseUrl: typeof source?.publicBaseUrl === 'string' ? source.publicBaseUrl : (prev.publicBaseUrl || ''),
    telegramBotToken: typeof source?.telegramBotToken === 'string' ? source.telegramBotToken : (prev.telegramBotToken || ''),
    telegramSupplyBotToken: typeof source?.telegramSupplyBotToken === 'string' ? source.telegramSupplyBotToken : (prev.telegramSupplyBotToken || ''),
    telegramSupergroupChatId: typeof source?.telegramSupergroupChatId === 'string' ? source.telegramSupergroupChatId : (prev.telegramSupergroupChatId || ''),
    telegramSupergroupEnabled: typeof source?.telegramSupergroupEnabled === 'boolean' ? source.telegramSupergroupEnabled : Boolean(prev.telegramSupergroupEnabled),
    telegramStageNotificationEmployeeIds: normalizeEmployeeIds(source?.telegramStageNotificationEmployeeIds ?? prev.telegramStageNotificationEmployeeIds),
    telegramRequestNotificationEmployeeIds: normalizeEmployeeIds(source?.telegramRequestNotificationEmployeeIds ?? prev.telegramRequestNotificationEmployeeIds),
    selfUpdateEnabled: typeof source?.selfUpdateEnabled === 'boolean' ? source.selfUpdateEnabled : Boolean(prev.selfUpdateEnabled),
    updateBranch: typeof source?.updateBranch === 'string' ? source.updateBranch : (prev.updateBranch || 'main'),
    updateRepositoryUrl: typeof source?.updateRepositoryUrl === 'string' ? source.updateRepositoryUrl : (prev.updateRepositoryUrl || ''),
    strictStageOwnershipEnabled: typeof source?.strictStageOwnershipEnabled === 'boolean' ? source.strictStageOwnershipEnabled : Boolean(prev.strictStageOwnershipEnabled),
    stageOwnershipBypassEmployeeIds: normalizeEmployeeIds(source?.stageOwnershipBypassEmployeeIds ?? prev.stageOwnershipBypassEmployeeIds),
    roleLabels: source?.roleLabels && typeof source.roleLabels === 'object' ? source.roleLabels : (prev.roleLabels || {}),
  };
}

function buildLegendSaveErrorMessage({
  summary,
  step,
  status,
  statusText,
  serverMessage,
  error,
  selectedStageLabel,
  selectedHeaderLabel,
  stagesCount,
  headersCount,
}) {
  const lines = [
    summary || 'Не удалось сохранить настройки этапов.',
    step ? `Шаг: ${step}` : '',
    status ? `HTTP: ${status}${statusText ? ` ${statusText}` : ''}` : '',
    serverMessage ? `Ответ сервера: ${serverMessage}` : '',
    error?.name ? `Тип ошибки: ${error.name}` : '',
    error?.message ? `Сообщение: ${error.message}` : '',
    selectedStageLabel ? `Выбранный этап: ${selectedStageLabel}` : '',
    selectedHeaderLabel ? `Выбранная колонка: ${selectedHeaderLabel}` : '',
    Number.isFinite(stagesCount) ? `Этапов в конфиге: ${stagesCount}` : '',
    Number.isFinite(headersCount) ? `Колонок в конфиге: ${headersCount}` : '',
  ];
  return lines.filter(Boolean).join('\n');
}

function Admin() {

  const navigate = useNavigate();
  const location = useLocation();
  const { roleTabs, allRoleTabs, refreshRoleConfig, getRoleMetaByKey } = useRoleConfig();
  const getDefaultEmployeeForm = (role = '') => ({
    ...emptyEmployeeForm,
    role: role || '',
  });
  const [activeRole, setActiveRole] = useState('general');
  const [selectedStepsRole, setSelectedStepsRole] = useState(() => roleTabs[0]?.key || '');
  const [stageManagerRoleKey, setStageManagerRoleKey] = useState('');
  const [steps, setSteps] = useState([]);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [installJob, setInstallJob] = useState(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [installingUpdates, setInstallingUpdates] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [appSettings, setAppSettings] = useState(() => buildAppSettingsState());
  const [telegramCheckResult, setTelegramCheckResult] = useState(null);
  const [checkingTelegramBot, setCheckingTelegramBot] = useState(false);
  const [settingTelegramWebhook, setSettingTelegramWebhook] = useState(false);
  const [telegramSupplyCheckResult, setTelegramSupplyCheckResult] = useState(null);
  const [checkingTelegramSupplyBot, setCheckingTelegramSupplyBot] = useState(false);
  const [settingTelegramSupplyWebhook, setSettingTelegramSupplyWebhook] = useState(false);
  const [showTelegramLogs, setShowTelegramLogs] = useState(false);
  const [telegramLogs, setTelegramLogs] = useState([]);
  const [telegramLogsLoading, setTelegramLogsLoading] = useState(false);
  const [clearingTelegramLogs, setClearingTelegramLogs] = useState(false);
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [clearingActivityLogs, setClearingActivityLogs] = useState(false);
  const [exportingBackup, setExportingBackup] = useState(false);
  const [importingBackup, setImportingBackup] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showTelegramNotificationModal, setShowTelegramNotificationModal] = useState(false);
  const [showStageOwnershipModal, setShowStageOwnershipModal] = useState(false);
  const [savingStageOwnership, setSavingStageOwnership] = useState(false);
  const [stageOwnershipEnabledDraft, setStageOwnershipEnabledDraft] = useState(false);
  const [stageOwnershipBypassDraftIds, setStageOwnershipBypassDraftIds] = useState([]);
  const [telegramNotificationDraftIds, setTelegramNotificationDraftIds] = useState([]);
  const [telegramStageNotificationDraftIds, setTelegramStageNotificationDraftIds] = useState([]);
  const [telegramNotificationActiveTab, setTelegramNotificationActiveTab] = useState('stages');
  const [employeeModalMode, setEmployeeModalMode] = useState('');
  const [stepModalMode, setStepModalMode] = useState('');
  const [showLegendColorModal, setShowLegendColorModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [savingTelegramNotifications, setSavingTelegramNotifications] = useState(false);
  const [savingStep, setSavingStep] = useState(false);
  const [savingLegendColors, setSavingLegendColors] = useState(false);
  const [orderStageLegendConfig, setOrderStageLegendConfig] = useState(() => buildOrderStageLegendConfig());
  const [legendEditForm, setLegendEditForm] = useState(null);
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [savingAppSettings, setSavingAppSettings] = useState(false);
  const [savingUpdateSettings, setSavingUpdateSettings] = useState(false);
  useGlobalErrorEffect(settingsError, 'Ошибка в настройках.');
  useGlobalErrorEffect(updateError, 'Ошибка обновления.');

  const [editStep, setEditStep] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [newStep, setNewStep] = useState({ stepName: '', description: '', order: 1 });
  const [newEmployee, setNewEmployee] = useState(() => getDefaultEmployeeForm());
  const [employeeSessionStatus, setEmployeeSessionStatus] = useState(null);
  const [employeeSessionRefreshing, setEmployeeSessionRefreshing] = useState(false);
  const [employeeSessionLastRefresh, setEmployeeSessionLastRefresh] = useState(null);
  const [employeeSessionMenuButtonCheckLoading, setEmployeeSessionMenuButtonCheckLoading] = useState(false);
  const [employeeSessionLastMenuButtonCheck, setEmployeeSessionLastMenuButtonCheck] = useState(null);
  const backupImportInputRef = useRef(null);
  const settingsTabs = buildSettingsTabs();
  const settingsTabKeySet = useMemo(() => new Set(settingsTabs.map((tab) => tab.key)), [settingsTabs]);
  const requestedSettingsTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const nextTab = String(params.get('tab') || '').trim();
    return settingsTabKeySet.has(nextTab) ? nextTab : 'general';
  }, [location.search, settingsTabKeySet]);
  const filteredSteps = steps.filter(s => s.role === selectedStepsRole).sort((a, b) => a.order - b.order);
  const employeeForm = employeeModalMode === 'edit' ? editEmployee : newEmployee;
  const legendItems = useMemo(() => {
    return orderStageLegendConfig.stages.map((item) => {
      return {
        ...item,
        hex: String(item.hex || item.defaultHex || '').trim() || item.defaultHex || '#FFFFFF',
      };
    });
  }, [orderStageLegendConfig]);
  const generalSubTabKeySet = useMemo(() => new Set(['updates', 'bot', 'access']), []);
  const [generalSubTab, setGeneralSubTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const sub = String(params.get('sub') || '').trim();
    const tab = String(params.get('tab') || '').trim();
    if (tab !== '' && tab !== 'general') return 'updates';
    return generalSubTabKeySet.has(sub) ? sub : 'updates';
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = String(params.get('tab') || '').trim();
    if (tab !== '' && tab !== 'general') {
      return;
    }
    const nextSub = String(params.get('sub') || '').trim();
    const normalizedSub = generalSubTabKeySet.has(nextSub) ? nextSub : 'updates';
    setGeneralSubTab(current => current === normalizedSub ? current : normalizedSub);
  }, [location.search, generalSubTabKeySet]);
  const handleGeneralSubTabChange = useCallback((nextSub) => {
    const normalizedSub = generalSubTabKeySet.has(nextSub) ? nextSub : 'updates';
    setGeneralSubTab(normalizedSub);
    const params = new URLSearchParams(location.search);
    params.set('tab', 'general');
    params.set('sub', normalizedSub);
    const search = params.toString();
    navigate(`/settings?${search}`, { replace: true });
  }, [navigate, location.search, generalSubTabKeySet]);
  const stageLegendMap = useMemo(() => {
    return legendItems.reduce((acc, item) => {
      acc[item.key] = item;
      return acc;
    }, {});
  }, [legendItems]);
  const productionHeaderPreviewItems = useMemo(() => {
    return (orderStageLegendConfig.secondaryHeaders || []).flatMap((item, index) => (
      item.legendKey && !item.useTableBackground
        ? [{
            ...item,
            headerIndex: index,
            previewHex: item.useTableBackground ? '#0C1A2A' : (item.hex || '#FFFFFF'),
            currentStageLabel: stageLegendMap[item.legendKey]?.label || 'Без этапа',
            previewTitle: item.label || 'Без названия',
          }]
        : []
    ));
  }, [orderStageLegendConfig.secondaryHeaders, stageLegendMap]);
  const hasModalWindowOpen = Boolean(
    employeeModalMode
    || stepModalMode
    || showLegendColorModal
    || stageManagerRoleKey
    || showTelegramLogs
    || showActivityLogs
    || showTelegramNotificationModal
    || showStageOwnershipModal
    || confirmAction,
  );
  const setEmployeeForm = (nextValue) => {
    if (employeeModalMode === 'edit') {
      setEditEmployee(nextValue);
      return;
    }
    setNewEmployee(nextValue);
  };
  const getOwnAllowedColumns = useCallback((entity, optionsByKey = null) => {
    const normalized = normalizeAllowedColumns(entity?.allowedColumns, { fallbackToAll: false });
    if (!optionsByKey) return normalized;
    return normalized.filter((columnKey) => Boolean(optionsByKey[columnKey]));
  }, []);
  const getEntityAllowedColumns = useCallback((entity, optionsByKey = null) => {
    const ownAllowedColumns = getOwnAllowedColumns(entity, optionsByKey);
    if (ownAllowedColumns.length > 0 || Array.isArray(entity?.allowedColumns)) {
      return ownAllowedColumns;
    }
    const fallbackRole = getRoleMetaByKey(String(entity?.role || '').trim());
    const fallbackAllowedColumns = normalizeAllowedColumns(fallbackRole?.allowedColumns, { fallbackToAll: false });
    if (!optionsByKey) {
      return fallbackAllowedColumns;
    }
    return fallbackAllowedColumns.filter((columnKey) => Boolean(optionsByKey[columnKey]));
  }, [getOwnAllowedColumns, getRoleMetaByKey]);
  const getAllowedColumnsSummary = (entity, { ownOnly = false, optionsByKey = roleColumnOptionsByKey, columnOptions = null } = {}) => {
    const allowedColumns = ownOnly
      ? getOwnAllowedColumns(entity, optionsByKey)
      : getEntityAllowedColumns(entity, optionsByKey);
    if (allowedColumns.length === 0) return 'Нет доступных колонок';
    const resolvedColumnOptions = Array.isArray(columnOptions) ? columnOptions : Object.values(optionsByKey || {});
    const uniqueOptions = [];
    const seenKeys = new Set();
    resolvedColumnOptions.forEach((column) => {
      if (!column) return;
      const equivalentKeys = Array.isArray(column.equivalentKeys) && column.equivalentKeys.length > 0
        ? column.equivalentKeys
        : [column.key];
      if (!equivalentKeys.some((columnKey) => allowedColumns.includes(columnKey))) return;
      if (seenKeys.has(column.key)) return;
      seenKeys.add(column.key);
      uniqueOptions.push(column);
    });
    const labels = uniqueOptions.map((column) => column.label);
    if (labels.length <= 3) return labels.join(', ');
    return `${labels.slice(0, 3).join(', ')} и еще ${labels.length - 3}`;
  };
  const roleColumnOptions = useMemo(() => {
    const secondaryHeaders = orderStageLegendConfig.secondaryHeaders || [];
    const getHeaderByPrimaryColumnIndex = (primaryColumnIndex = -1) => {
      if (primaryColumnIndex < 0) return null;
      let startIndex = 0;
      for (const header of secondaryHeaders) {
        const span = Number(header?.colSpan) || 1;
        const endIndex = startIndex + span - 1;
        if (primaryColumnIndex >= startIndex && primaryColumnIndex <= endIndex) {
          return header;
        }
        startIndex += span;
      }
      return null;
    };

    return ROLE_COLUMN_ACCESS_OPTIONS.map((column) => {
      const header = getHeaderByPrimaryColumnIndex(column.primaryColumnIndex);
      return {
        ...column,
        headerLabel: header?.label || '',
        legendKey: header?.legendKey || '',
        useTableBackground: Boolean(header?.useTableBackground),
        previewColor: header?.useTableBackground ? '#0C1A2A' : (header?.hex || '#DCEBFA'),
      };
    });
  }, [orderStageLegendConfig]);
  const employeeColumnOptions = useMemo(() => {
    const groupedOptions = new Map();
    roleColumnOptions
      .filter((column) => (
        !EMPLOYEE_HIDDEN_COLUMN_KEYS.has(column.key)
        && column.legendKey
        && !column.useTableBackground
      ))
      .forEach((column) => {
        const label = column.headerLabel || column.label;
        const groupKey = `${label}::${column.legendKey}`;
        const current = groupedOptions.get(groupKey);
        if (!current) {
          groupedOptions.set(groupKey, {
            ...column,
            label,
            description: '',
            equivalentKeys: [column.key],
          });
          return;
        }
        current.equivalentKeys = Array.from(new Set([...(current.equivalentKeys || []), column.key]));
      });
    return Array.from(groupedOptions.values());
  }, [roleColumnOptions]);
  const roleColumnOptionsByKey = useMemo(() => {
    return roleColumnOptions.reduce((acc, column) => {
      acc[column.key] = column;
      return acc;
    }, {});
  }, [roleColumnOptions]);
  const employeeColumnOptionsByKey = useMemo(() => {
    return employeeColumnOptions.reduce((acc, column) => {
      const keys = Array.isArray(column.equivalentKeys) && column.equivalentKeys.length > 0
        ? column.equivalentKeys
        : [column.key];
      keys.forEach((key) => {
        acc[key] = column;
      });
      acc[column.key] = column;
      return acc;
    }, {});
  }, [employeeColumnOptions]);
  const renderAllowedColumnsMarkers = (entity, { compact = false, ownOnly = false, optionsByKey = roleColumnOptionsByKey } = {}) => {
    const allowedColumns = ownOnly
      ? getOwnAllowedColumns(entity, optionsByKey)
      : getEntityAllowedColumns(entity, optionsByKey);
    if (allowedColumns.length === 0) {
      return <span className="text-subtle">Нет доступа</span>;
    }
    const uniqueColumns = [];
    const seenKeys = new Set();
    allowedColumns.forEach((columnKey) => {
      const column = optionsByKey[columnKey];
      if (!column || seenKeys.has(column.key)) return;
      seenKeys.add(column.key);
      uniqueColumns.push(column);
    });
    return (
      <div
        className={`role-allowed-columns-markers ${compact ? 'role-allowed-columns-markers-compact' : ''}`}
        title={getAllowedColumnsSummary(entity, { ownOnly, optionsByKey })}
      >
        {uniqueColumns.map((column) => {
          return (
            <span
              key={column.key}
              className="role-allowed-column-marker"
              title={`${column.label}: ${column.description}`}
              style={{
                background: column.previewColor,
                width: column.widthVar ? `clamp(18px, calc(var(${column.widthVar}) / 4), 64px)` : undefined,
              }}
              aria-label={column.label}
            />
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    setEditStep(null);
    setEditEmployee(null);
    setEmployeeModalMode('');
    setStepModalMode('');
    setNewStep({ stepName: '', description: '', order: filteredSteps.length + 1 });
    setNewEmployee(getDefaultEmployeeForm());
    setSettingsError('');
    setSettingsSuccess('');
  }, [activeRole, filteredSteps.length, selectedStepsRole]);

  useEffect(() => {
    if (!settingsError) {
      setModalErrorMessage('');
      return;
    }
    if (hasModalWindowOpen) {
      setModalErrorMessage(settingsError);
    }
  }, [hasModalWindowOpen, settingsError]);

  useEffect(() => {
    fetchEmployees().catch(error => setSettingsError(toUserErrorMessage(error, 'Не удалось загрузить сотрудников.')));
    fetchAppSettings().catch(error => setSettingsError(toUserErrorMessage(error, 'Не удалось загрузить настройки.')));
  }, []);

  useEffect(() => {
    if (!roleTabs.length) {
      setSelectedStepsRole('');
      return;
    }

    if (!selectedStepsRole || !roleTabs.some(role => role.key === selectedStepsRole)) {
      setSelectedStepsRole(roleTabs[0]?.key || '');
    }
  }, [roleTabs, selectedStepsRole]);

  useEffect(() => {
    if (activeRole !== requestedSettingsTab) {
      setActiveRole(requestedSettingsTab);
    }
  }, [activeRole, requestedSettingsTab]);

  useEffect(() => {
    fetchSteps();
    fetchOrderStageLegendConfig().catch(error => setSettingsError(toUserErrorMessage(error, 'Не удалось загрузить легенду этапов.')));
    fetchUpdateStatus();
  }, []);

  useEffect(() => {
    if (!installingUpdates) {
      return undefined;
    }

    const pollInstallStatus = () => {
      fetchInstallJobStatus({ silent: true });
    };

    pollInstallStatus();
    const intervalId = window.setInterval(pollInstallStatus, 3000);
    return () => window.clearInterval(intervalId);
  }, [installingUpdates]);

  useEffect(() => {
    const refreshOverview = () => {
      fetchSteps();
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      refreshOverview();
    };

    const intervalId = window.setInterval(refreshOverview, 10000);
    window.addEventListener('focus', refreshOverview);
    document.addEventListener('visibilitychange', handleVisibilityRefresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshOverview);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, []);

  const fetchEmployeeSessionStatus = useCallback(async (employeeId) => {
    try {
      const normalized = String(employeeId || '').trim();
      if (!normalized) {
        setEmployeeSessionStatus(null);
        return;
      }
      const res = await apiFetch(`/api/telegram/employee/${encodeURIComponent(normalized)}/session-status`);
      const data = await parseJsonSafely(res);
      if (res.ok && data?.ok) {
        setEmployeeSessionStatus(data || null);
      } else {
        setEmployeeSessionStatus({
          employeeId: normalized,
          statusLevel: 'warning',
          statusLabel: data?.message || 'Не удалось получить статус токена.',
          ok: false,
        });
      }
    } catch (_err) {
      setEmployeeSessionStatus({
        employeeId: String(employeeId || ''),
        statusLevel: 'warning',
        statusLabel: 'Сетевая ошибка при получении статуса токена.',
        ok: false,
      });
    }
  }, []);

  useEffect(() => {
    if (employeeModalMode !== 'edit' || !editEmployee?._id) {
      setEmployeeSessionStatus(null);
      setEmployeeSessionLastRefresh(null);
      setEmployeeSessionLastMenuButtonCheck(null);
      return undefined;
    }
    let cancelled = false;
    setEmployeeSessionStatus(null);
    setEmployeeSessionLastRefresh(null);
    setEmployeeSessionLastMenuButtonCheck(null);
    fetchEmployeeSessionStatus(editEmployee._id).then(() => { if (cancelled) return null; return null; }).catch(() => {});
    return () => { cancelled = true; };
  }, [employeeModalMode, editEmployee?._id, fetchEmployeeSessionStatus]);

  const handleRefreshEmployeeSessionToken = useCallback(async () => {
    const employeeId = String(editEmployee?._id || '').trim();
    if (!employeeId) return;
    setEmployeeSessionRefreshing(true);
    setSettingsError('');
    setEmployeeSessionLastRefresh(null);
    try {
      const res = await apiFetch('/api/telegram/employee/refresh-session-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId }),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || 'Не удалось продлить токен.');
      }
      setEmployeeSessionLastRefresh(data || null);
      await fetchEmployeeSessionStatus(employeeId);
      fetchEmployees().catch(() => {});
      setSettingsSuccess(`Токен сотрудника продлён на ${Number(data?.ttlDays || 1825) || 1825} дней.`);
    } catch (err) {
      const msg = err?.message || err?.toString?.() || 'Не удалось продлить токен.';
      setSettingsError(msg || 'Ошибка продления токена.');
    } finally {
      setEmployeeSessionRefreshing(false);
    }
  }, [editEmployee?._id, fetchEmployeeSessionStatus]);

  const handleCheckEmployeeMenuButton = useCallback(async () => {
    const employeeId = String(editEmployee?._id || '').trim();
    if (!employeeId) return;
    setEmployeeSessionMenuButtonCheckLoading(true);
    setSettingsError('');
    setEmployeeSessionLastMenuButtonCheck(null);
    try {
      const res = await apiFetch('/api/telegram/employee/get-menu-button', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId }),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || 'Не удалось проверить Menu Button.');
      }
      setEmployeeSessionLastMenuButtonCheck(data || null);
      if (data?.hasEmployeeToken) {
        setSettingsSuccess('✅ Menu Button сотрудника настроен: URL содержит employeeSessionToken!');
      } else if (data?.type === 'web_app') {
        setSettingsError('⚠️ Menu Button = web_app, но токен employeeSessionToken отсутствует в URL.');
      } else if (data?.type === 'default' || !data?.type) {
        setSettingsError('❌ Menu Button не настроен (default / none).');
      } else {
        setSettingsSuccess('Menu Button проверен.');
      }
    } catch (err) {
      const msg = err?.message || err?.toString?.() || 'Не удалось проверить Menu Button.';
      setSettingsError(msg || 'Ошибка проверки Menu Button.');
    } finally {
      setEmployeeSessionMenuButtonCheckLoading(false);
    }
  }, [editEmployee?._id]);

  const fetchSteps = async () => {
    const res = await apiFetch('/api/processSteps');
    const data = await parseJsonSafely(res);
    setSteps(Array.isArray(data) ? data : []);
  };

  const fetchOrderStageLegendConfig = async () => {
    const res = await apiFetch('/api/order-stage-legend-config');
    const data = await parseJsonSafely(res);
    if (!res.ok) {
      throw new Error(data?.message || 'Не удалось загрузить конфигурацию легенды этапов.');
    }
    setOrderStageLegendConfig(buildOrderStageLegendConfig(data || {}));
  };

  const fetchAppSettings = async () => {
    const res = await apiFetch('/api/settings');
    const data = await parseJsonSafely(res);
    if (!res.ok) {
      throw new Error(data?.message || 'Не удалось загрузить настройки.');
    }
    setAppSettings((prev) => buildAppSettingsState(data, prev));
  };

  const fetchEmployees = async () => {
    const res = await apiFetch('/api/employees');
    const data = await parseJsonSafely(res);
    if (!res.ok) {
      throw new Error(data?.message || 'Не удалось загрузить сотрудников.');
    }
    setEmployees(Array.isArray(data) ? data : []);
  };

  const fetchUpdateStatus = async () => {
    setCheckingUpdates(true);
    setUpdateError('');
    try {
      const res = await apiFetch('/api/updates/status');
      const data = await parseJsonSafely(res);
      if (!res.ok) throw new Error(data?.message || 'Не удалось проверить обновления');
      setUpdateStatus(data);
      setInstallJob(data?.installJob || null);
      setInstallingUpdates(Boolean(data?.installInProgress));
      setUpdateMessage(data?.message || '');
    } catch (error) {
      setUpdateError(toUserErrorMessage(error, 'Не удалось проверить обновления.'));
    } finally {
      setCheckingUpdates(false);
    }
  };

  const fetchInstallJobStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setUpdateError('');
    }
    try {
      const res = await apiFetch('/api/updates/install-status');
      const data = await parseJsonSafely(res);
      if (!res.ok) throw new Error(data?.message || 'Не удалось получить статус установки обновлений');

      const nextJob = data?.installJob || null;
      const inProgress = Boolean(data?.installInProgress && nextJob?.status === 'running');
      setInstallJob(nextJob);
      setInstallingUpdates(inProgress);

      if (!nextJob) {
        return;
      }

      if (nextJob.status === 'completed') {
        setUpdateMessage(nextJob.message || 'Обновления установлены');
        setUpdateError('');
        if (nextJob.statusAfter) {
          setUpdateStatus(nextJob.statusAfter);
        } else if (!inProgress) {
          fetchUpdateStatus();
        }
        return;
      }

      if (nextJob.status === 'failed') {
        setUpdateError(toUserErrorMessage(nextJob.details || nextJob.message, 'Не удалось установить обновления.'));
        if (nextJob.statusAfter) {
          setUpdateStatus(nextJob.statusAfter);
        } else if (!inProgress) {
          fetchUpdateStatus();
        }
      }
      return { nextJob, inProgress };
    } catch (error) {
      if (!silent) {
        setUpdateError(toUserErrorMessage(error, 'Не удалось получить статус установки обновлений.'));
      }
      return { nextJob: null, inProgress: false };
    }
  };

  const installUpdates = async () => {
    setInstallingUpdates(true);
    setUpdateError('');
    try {
      const res = await apiFetch('/api/updates/install', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (res.status === 504) {
        const installState = await fetchInstallJobStatus({ silent: true });
        if (installState?.inProgress) {
          setUpdateMessage('Установка обновлений уже запущена. Продолжаю отслеживать её статус после ответа прокси с таймаутом.');
          return;
        }
      }
      if (res.status === 409 && data?.installJob) {
        setInstallJob(data.installJob);
        setInstallingUpdates(data.installJob.status === 'running');
        setUpdateMessage(data?.message || 'Установка обновлений уже выполняется.');
        return;
      }
      if (!res.ok) throw new Error(data?.details || data?.message || 'Не удалось запустить установку обновлений');
      if (data?.status) {
        setUpdateStatus(data.status);
      }
      setInstallJob(data?.installJob || null);
      setInstallingUpdates(Boolean(data?.installJob?.status === 'running' || res.status === 202));
      setUpdateMessage(data?.message || 'Установка обновлений запущена');
    } catch (error) {
      const errorText = toUserErrorMessage(error, 'Не удалось установить обновления.');
      if (errorText.includes('504 Gateway Time-out')) {
        const installState = await fetchInstallJobStatus({ silent: true });
        if (installState?.inProgress) {
          setUpdateMessage('Установка обновлений уже запущена. Продолжаю отслеживать её статус после ответа прокси с таймаутом.');
          return;
        }
      }
      setUpdateError(errorText);
      setInstallingUpdates(false);
    }
  };

  const saveUpdateSettings = async () => {
    if (savingUpdateSettings) return;
    setUpdateError('');
    setUpdateMessage('');
    setSavingUpdateSettings(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appSettings),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok) throw new Error(data?.message || 'Не удалось сохранить настройки обновления');
      setAppSettings((prev) => buildAppSettingsState(data, prev));
      await refreshRoleConfig();
      setUpdateMessage('Настройки обновления сохранены.');
      await fetchUpdateStatus();
    } catch (error) {
      setUpdateError(toUserErrorMessage(error, 'Не удалось сохранить настройки обновления.'));
    } finally {
      setSavingUpdateSettings(false);
    }
  };

  const getRoleLabel = (role) => {
    return allRoleTabs.find(item => item.key === role)?.label || role;
  };

  const handleSettingsTabChange = useCallback((nextTab) => {
    const normalizedTab = settingsTabKeySet.has(nextTab) ? nextTab : 'general';
    setActiveRole(normalizedTab);
    navigate(normalizedTab === 'general' ? '/settings' : `/settings?tab=${normalizedTab}`, { replace: true });
  }, [navigate, settingsTabKeySet]);

  const getEmployeeTelegramSummary = (employee) => {
    if (!employee.telegramUserId) {
      return '—';
    }

    return (
      <div>
        <div>{employee.telegramUsername || 'без username'}</div>
        <div className="text-small text-subtle">
          {employee.telegramFirstName || ''} {employee.telegramLastName || ''}
          {employee.telegramFirstName || employee.telegramLastName ? ' ' : ''}ID: {employee.telegramUserId}
        </div>
      </div>
    );
  };

  const telegramReadyEmployees = useMemo(() => (
    employees.filter((employee) => String(employee.telegramChatId || '').trim())
  ), [employees]);

  const sortedEmployees = useMemo(() => (
    [...employees].sort((left, right) => String(left?.fullName || '').localeCompare(String(right?.fullName || '').trim(), 'ru', { sensitivity: 'base' }))
  ), [employees]);

  const telegramReadyEmployeeIds = useMemo(() => (
    new Set(telegramReadyEmployees.map((employee) => String(employee._id || '').trim()).filter(Boolean))
  ), [telegramReadyEmployees]);

  const telegramNotificationEmployeeIdsSet = useMemo(() => (
    new Set(
      (Array.isArray(appSettings.telegramRequestNotificationEmployeeIds) ? appSettings.telegramRequestNotificationEmployeeIds : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    )
  ), [appSettings.telegramRequestNotificationEmployeeIds]);

  const openStageOwnershipModal = useCallback(() => {
    setStageOwnershipEnabledDraft(Boolean(appSettings.strictStageOwnershipEnabled));
    setStageOwnershipBypassDraftIds(normalizeEmployeeIds(appSettings.stageOwnershipBypassEmployeeIds));
    setSettingsError('');
    setSettingsSuccess('');
    setShowStageOwnershipModal(true);
  }, [appSettings]);

  const closeStageOwnershipModal = useCallback(() => {
    if (savingStageOwnership) return;
    setShowStageOwnershipModal(false);
    setStageOwnershipEnabledDraft(Boolean(appSettings.strictStageOwnershipEnabled));
    setStageOwnershipBypassDraftIds(normalizeEmployeeIds(appSettings.stageOwnershipBypassEmployeeIds));
  }, [appSettings, savingStageOwnership]);

  const saveStageOwnershipSettings = useCallback(async () => {
    if (savingStageOwnership) return;
    setSavingStageOwnership(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const nextSettings = buildAppSettingsState({
        ...appSettings,
        strictStageOwnershipEnabled: Boolean(stageOwnershipEnabledDraft),
        stageOwnershipBypassEmployeeIds: normalizeEmployeeIds(stageOwnershipBypassDraftIds),
      }, appSettings);
      const res = await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextSettings),
      });
      const payload = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(payload?.message || 'Не удалось сохранить настройки');
      }
      const persisted = payload?.data?.settings || payload?.settings || payload || {};
      setAppSettings((prev) => buildAppSettingsState(persisted, prev));
      setStageOwnershipEnabledDraft(Boolean(persisted?.strictStageOwnershipEnabled));
      setStageOwnershipBypassDraftIds(normalizeEmployeeIds(persisted?.stageOwnershipBypassEmployeeIds));
      setShowStageOwnershipModal(false);
      setSettingsSuccess('Настройки сохранены');
    } catch (err) {
      setSettingsError(toUserErrorMessage(err));
    } finally {
      setSavingStageOwnership(false);
    }
  }, [appSettings, savingStageOwnership, stageOwnershipBypassDraftIds, stageOwnershipEnabledDraft]);

  const openTelegramNotificationModal = () => {
    setTelegramStageNotificationDraftIds(
      (Array.isArray(appSettings.telegramStageNotificationEmployeeIds) ? appSettings.telegramStageNotificationEmployeeIds : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    );
    setTelegramNotificationDraftIds(
      (Array.isArray(appSettings.telegramRequestNotificationEmployeeIds) ? appSettings.telegramRequestNotificationEmployeeIds : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    );
    setTelegramNotificationActiveTab((current) => (current === 'stages' || current === 'requests' ? current : 'stages'));
    setShowTelegramNotificationModal(true);
    setSettingsError('');
    setSettingsSuccess('');
  };

  const closeTelegramNotificationModal = () => {
    if (savingTelegramNotifications) return;
    setShowTelegramNotificationModal(false);
  };

  const toggleTelegramNotificationEmployee = (employeeId, enabled) => {
    const normalizedEmployeeId = String(employeeId || '').trim();
    if (!normalizedEmployeeId) return;
    if (telegramNotificationActiveTab === 'stages') {
      setTelegramStageNotificationDraftIds((current) => {
        const next = new Set((Array.isArray(current) ? current : []).map((item) => String(item || '').trim()).filter(Boolean));
        if (enabled) {
          next.add(normalizedEmployeeId);
        } else {
          next.delete(normalizedEmployeeId);
        }
        return Array.from(next);
      });
      return;
    }
    setTelegramNotificationDraftIds((current) => {
      const next = new Set((Array.isArray(current) ? current : []).map((item) => String(item || '').trim()).filter(Boolean));
      if (enabled) {
        next.add(normalizedEmployeeId);
      } else {
        next.delete(normalizedEmployeeId);
      }
      return Array.from(next);
    });
  };

  const saveTelegramNotificationSettings = async () => {
    if (savingTelegramNotifications) return;
    setSavingTelegramNotifications(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const filteredStageIds = telegramStageNotificationDraftIds.filter((employeeId) => telegramReadyEmployeeIds.has(String(employeeId || '').trim()));
      const filteredRequestIds = telegramNotificationDraftIds.filter((employeeId) => telegramReadyEmployeeIds.has(String(employeeId || '').trim()));
      const res = await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...appSettings,
          telegramStageNotificationEmployeeIds: filteredStageIds,
          telegramRequestNotificationEmployeeIds: filteredRequestIds,
        }),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось сохранить получателей Telegram-уведомлений.');
      }

      setAppSettings((prev) => buildAppSettingsState(data, prev));
      setTelegramStageNotificationDraftIds(Array.isArray(data?.telegramStageNotificationEmployeeIds) ? data.telegramStageNotificationEmployeeIds : []);
      setTelegramNotificationDraftIds(Array.isArray(data?.telegramRequestNotificationEmployeeIds) ? data.telegramRequestNotificationEmployeeIds : []);
      setShowTelegramNotificationModal(false);
      setSettingsSuccess('Получатели уведомлений в Telegram сохранены.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось сохранить получателей Telegram-уведомлений.'));
    } finally {
      setSavingTelegramNotifications(false);
    }
  };

  const formatActivityLogEntry = (entry) => {
    const timestamp = entry?.createdAt ? formatDateTimeDisplay(entry.createdAt) : 'Без времени';
    const actor = entry?.actor?.label || 'Система';
    const message = entry?.message || entry?.action || 'Событие';
    const details = entry?.details && typeof entry.details === 'object'
      ? JSON.stringify(entry.details, null, 2)
      : String(entry?.details || '');
    return `[${timestamp}] ${actor}\n${message}\n${details}`;
  };

  const saveAppSettings = async () => {
    if (savingAppSettings) return;
    setSettingsError('');
    setSettingsSuccess('');
    setSavingAppSettings(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appSettings),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        setSettingsError(toUserErrorMessage(data?.message, 'Не удалось сохранить настройки.'));
        return;
      }
      setAppSettings((prev) => buildAppSettingsState(data, prev));
      await refreshRoleConfig();
      setSettingsSuccess('Настройки сохранены.');
      await fetchUpdateStatus();
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось сохранить настройки.'));
    } finally {
      setSavingAppSettings(false);
    }
  };

  const checkTelegramBot = async () => {
    setCheckingTelegramBot(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/check', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось проверить Telegram-бота.');
      }
      setTelegramCheckResult(data);
      setSettingsSuccess(`Бот ${data?.bot?.firstName || ''} @${data?.bot?.username || ''} успешно проверен.`.trim());
    } catch (error) {
      setTelegramCheckResult(null);
      setSettingsError(toUserErrorMessage(error, 'Не удалось проверить Telegram-бота.'));
    } finally {
      setCheckingTelegramBot(false);
    }
  };

  const setupTelegramWebhook = async () => {
    setSettingTelegramWebhook(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/webhook/setup', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось установить webhook Telegram-бота.');
      }
      setTelegramCheckResult(data);
      setSettingsSuccess(data?.message || 'Webhook Telegram-бота установлен.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось установить webhook Telegram-бота.'));
    } finally {
      setSettingTelegramWebhook(false);
    }
  };

  const [refreshingAuthorizedMenuButtons, setRefreshingAuthorizedMenuButtons] = useState(false);

  const refreshAuthorizedTelegramMenuButtons = async () => {
    setRefreshingAuthorizedMenuButtons(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/refresh-authorized', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось обновить кнопки Telegram сотрудникам.');
      }
      setSettingsSuccess(data?.message || 'Кнопки Telegram для сотрудников обновлены.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось обновить кнопки Telegram сотрудникам.'));
    } finally {
      setRefreshingAuthorizedMenuButtons(false);
    }
  };

  const checkTelegramSupplyBot = async () => {
    setCheckingTelegramSupplyBot(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/supply/check', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось проверить Telegram-бота снабжения.');
      }
      setTelegramSupplyCheckResult(data);
      setSettingsSuccess(`Бот снабжения ${data?.bot?.firstName || ''} @${data?.bot?.username || ''} успешно проверен.`.trim());
    } catch (error) {
      setTelegramSupplyCheckResult(null);
      setSettingsError(toUserErrorMessage(error, 'Не удалось проверить Telegram-бота снабжения.'));
    } finally {
      setCheckingTelegramSupplyBot(false);
    }
  };

  const setupTelegramSupplyWebhook = async () => {
    setSettingTelegramSupplyWebhook(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/supply/webhook/setup', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось установить webhook бота снабжения.');
      }
      setTelegramSupplyCheckResult(data);
      setSettingsSuccess(data?.message || 'Webhook бота снабжения установлен.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось установить webhook бота снабжения.'));
    } finally {
      setSettingTelegramSupplyWebhook(false);
    }
  };

  const [refreshingAuthorizedSupplyMenuButtons, setRefreshingAuthorizedSupplyMenuButtons] = useState(false);

  const refreshAuthorizedSupplyTelegramMenuButtons = async () => {
    setRefreshingAuthorizedSupplyMenuButtons(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/supply/refresh-authorized', { method: 'POST' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось обновить кнопки бота снабжения.');
      }
      setSettingsSuccess(data?.message || 'Кнопки бота снабжения для сотрудников обновлены.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось обновить кнопки бота снабжения.'));
    } finally {
      setRefreshingAuthorizedSupplyMenuButtons(false);
    }
  };

  const fetchTelegramLogs = async ({ openModal = false } = {}) => {
    setTelegramLogsLoading(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/logs?limit=200');
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось загрузить логи ТГ бота.');
      }
      setTelegramLogs(Array.isArray(data?.logs) ? data.logs : []);
      if (openModal) {
        setShowTelegramLogs(true);
      }
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось загрузить логи ТГ бота.'));
      if (openModal) {
        setShowTelegramLogs(true);
      }
    } finally {
      setTelegramLogsLoading(false);
    }
  };

  const clearTelegramLogs = async () => {
    if (clearingTelegramLogs) return;
    setClearingTelegramLogs(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/telegram/logs', { method: 'DELETE' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось очистить логи ТГ бота.');
      }
      setTelegramLogs([]);
      setSettingsSuccess(data?.message || 'Логи ТГ бота очищены.');
      return true;
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось очистить логи ТГ бота.'));
      return false;
    } finally {
      setClearingTelegramLogs(false);
    }
  };

  const fetchActivityLogs = async ({ openModal = false } = {}) => {
    setActivityLogsLoading(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/activity-logs?limit=200');
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось загрузить журнал действий.');
      }
      setActivityLogs(Array.isArray(data?.logs) ? data.logs : []);
      if (openModal) {
        setShowActivityLogs(true);
      }
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось загрузить журнал действий.'));
      if (openModal) {
        setShowActivityLogs(true);
      }
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const clearActivityLogs = async () => {
    if (clearingActivityLogs) return;
    setClearingActivityLogs(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/activity-logs', { method: 'DELETE' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось очистить журнал действий.');
      }
      setActivityLogs([]);
      setSettingsSuccess(data?.message || 'Журнал действий очищен.');
      return true;
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось очистить журнал действий.'));
      return false;
    } finally {
      setClearingActivityLogs(false);
    }
  };

  const exportBackup = async () => {
    if (exportingBackup) return;
    setExportingBackup(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch('/api/backup/export');
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, 'Не удалось экспортировать резервную копию.'));
      }
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const fileNameMatch = disposition.match(/filename="([^"]+)"/i);
      const fileName = fileNameMatch?.[1] || `kaznadzei-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSettingsSuccess('Резервная копия экспортирована.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось экспортировать резервную копию.'));
    } finally {
      setExportingBackup(false);
    }
  };

  const openBackupImportPicker = () => {
    if (importingBackup) return;
    backupImportInputRef.current?.click();
  };

  const handleBackupImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const confirmed = window.confirm(`Импортировать резервную копию "${file.name}"? Текущие данные проекта будут заменены.`);
    if (!confirmed) return;

    setImportingBackup(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const fileText = await file.text();
      const payload = JSON.parse(fileText);
      const res = await apiFetch('/api/backup/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.message || 'Не удалось импортировать резервную копию.');
      }
      await Promise.all([
        fetchSteps(),
        fetchEmployees().catch(() => {}),
        fetchAppSettings().catch(() => {}),
        fetchUpdateStatus().catch(() => {}),
        refreshRoleConfig().catch(() => {}),
      ]);
      setTelegramCheckResult(null);
      setTelegramLogs([]);
      setSettingsSuccess(data?.message || 'Резервная копия импортирована.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось импортировать резервную копию.'));
    } finally {
      setImportingBackup(false);
    }
  };

  const formatTelegramLogEntry = (entry) => {
    const timestamp = entry?.createdAt ? formatDateTimeDisplay(entry.createdAt) : 'Без времени';
    const scope = entry?.scope || 'telegram';
    const event = entry?.event || 'event';
    const details = entry?.details && typeof entry.details === 'object'
      ? JSON.stringify(entry.details, null, 2)
      : String(entry?.details || '');
    return `[${timestamp}] [${scope}] ${event}\n${details}`;
  };

  const resetEmployeeForm = () => {
    setEditEmployee(null);
    setNewEmployee(getDefaultEmployeeForm());
  };

  const openCreateEmployeeModal = () => {
    resetEmployeeForm();
    setEmployeeModalMode('create');
    setSettingsError('');
    setSettingsSuccess('');
  };

  const openEditEmployeeModal = (employee) => {
    let normalizedKinds = Array.isArray(employee?.telegramBotKinds) ? [...employee.telegramBotKinds] : [];
    if (!normalizedKinds.length) {
      normalizedKinds = String(employee?.telegramBotKind || 'primary').trim() === 'supply' ? ['supply'] : ['primary'];
    }
    normalizedKinds = Array.from(new Set(normalizedKinds.filter((k) => k === 'primary' || k === 'supply')));
    if (!normalizedKinds.length) normalizedKinds = ['primary'];
    setEditEmployee({
      ...getDefaultEmployeeForm(employee.role),
      ...employee,
      telegramBotKinds: normalizedKinds,
      telegramBotKind: normalizedKinds.includes('supply') && !normalizedKinds.includes('primary') ? 'supply' : 'primary',
      allowedColumns: [...getOwnAllowedColumns(employee, employeeColumnOptionsByKey)],
    });
    setEmployeeModalMode('edit');
    setSettingsError('');
    setSettingsSuccess('');
  };

  const closeEmployeeModal = () => {
    if (savingEmployee) return;
    setEmployeeModalMode('');
    resetEmployeeForm();
  };

  const closeStageManager = () => {
    if (savingStep) return;
    setStageManagerRoleKey('');
  };

  const openCreateStepModal = () => {
    setEditStep(null);
    setNewStep({ stepName: '', description: '', order: filteredSteps.length + 1 });
    setStepModalMode('create');
    setSettingsError('');
    setSettingsSuccess('');
  };

  const openEditStepModal = (step) => {
    setEditStep({ ...step });
    setStepModalMode('edit');
    setSettingsError('');
    setSettingsSuccess('');
  };

  const closeStepModal = () => {
    if (savingStep) return;
    setStepModalMode('');
    setEditStep(null);
    setNewStep({ stepName: '', description: '', order: filteredSteps.length + 1 });
  };

  const openLegendColorModal = (targetHeaderIndex = null) => {
    const headerIndex = Number.isInteger(targetHeaderIndex) ? targetHeaderIndex : -1;
    const targetHeader = orderStageLegendConfig.secondaryHeaders?.[headerIndex];
    if (!targetHeader) return;
    const linkedStage = stageLegendMap[targetHeader.legendKey] || null;
    setLegendEditForm({
      headerIndex,
      label: targetHeader.label || '',
      legendKey: targetHeader.legendKey || '',
      stageLabel: linkedStage?.label || 'Без этапа',
      useTableBackground: Boolean(targetHeader.useTableBackground),
      hex: targetHeader.useTableBackground ? '#0C1A2A' : (targetHeader.hex || '#FFFFFF'),
    });
    setShowLegendColorModal(true);
    setSettingsError('');
    setSettingsSuccess('');
  };

  const closeLegendColorModal = () => {
    if (savingLegendColors) return;
    setShowLegendColorModal(false);
    setLegendEditForm(null);
  };

  useEscapeKey(() => {
    if (confirmAction && !confirmLoading) {
      setConfirmAction(null);
      return;
    }
    if (showActivityLogs) {
      setShowActivityLogs(false);
      return;
    }
    if (showTelegramLogs) {
      setShowTelegramLogs(false);
      return;
    }
    if (showTelegramNotificationModal) {
      closeTelegramNotificationModal();
      return;
    }
    if (showLegendColorModal && !savingLegendColors) {
      closeLegendColorModal();
      return;
    }
    if (stageManagerRoleKey && !savingStep) {
      closeStageManager();
    }
  }, Boolean(
    confirmAction
    || showActivityLogs
    || showTelegramLogs
    || showTelegramNotificationModal
    || showLegendColorModal
    || stageManagerRoleKey
  ));

  const requestDeleteAction = (action) => {
    setSettingsError('');
    setSettingsSuccess('');
    setConfirmAction(action);
  };

  const requestDeleteEmployee = (employee) => {
    requestDeleteAction({
      type: 'employee',
      id: employee._id,
      title: 'Удалить сотрудника?',
      message: `Сотрудник "${employee.fullName}" будет удален. Если он уже привязан к Telegram, связь тоже придется настраивать заново.`,
      confirmLabel: 'Удалить сотрудника',
    });
  };

  const requestDeleteStep = (step) => {
    requestDeleteAction({
      type: 'step',
      id: step._id,
      title: 'Удалить этап?',
      message: `Этап "${step.stepName}" будет удален из роли "${getRoleLabel(step.role)}". Проверьте, что он больше не нужен в рабочих сценариях.`,
      confirmLabel: 'Удалить этап',
    });
  };

  const requestClearTelegramLogs = () => {
    requestDeleteAction({
      type: 'clearTelegramLogs',
      title: 'Очистить лог ТГ бота?',
      message: `Все записи в логе ТГ бота будут удалены без возможности восстановления.`,
      confirmLabel: 'Очистить лог',
    });
  };

  const requestClearActivityLogs = () => {
    requestDeleteAction({
      type: 'clearActivityLogs',
      title: 'Очистить журнал действий?',
      message: `Все записи журнала действий будут удалены без возможности восстановления.`,
      confirmLabel: 'Очистить журнал',
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmAction) return;
    if (confirmLoading) return;
    setConfirmLoading(true);
    let success = false;
    try {
      if (confirmAction.type === 'employee') {
        success = await handleDeleteEmployee(confirmAction.id);
      } else if (confirmAction.type === 'step') {
        success = await handleDeleteStep(confirmAction.id);
      } else if (confirmAction.type === 'clearTelegramLogs') {
        success = await clearTelegramLogs();
      } else if (confirmAction.type === 'clearActivityLogs') {
        success = await clearActivityLogs();
      }
    } finally {
      setConfirmLoading(false);
    }
    if (success) {
      setConfirmAction(null);
    }
  };

  const handleAddEmployee = async () => {
    setSettingsError('');
    setSettingsSuccess('');
    setSavingEmployee(true);
    try {
      const payload = {
        ...newEmployee,
        allowedColumns: getOwnAllowedColumns(newEmployee, employeeColumnOptionsByKey),
      };
      const res = await apiFetch('/api/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setSettingsError(await getErrorMessage(res, 'Не удалось добавить сотрудника.'));
        return;
      }
      resetEmployeeForm();
      setEmployeeModalMode('');
      await fetchEmployees();
      setSettingsSuccess('Сотрудник добавлен.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось добавить сотрудника.'));
    } finally {
      setSavingEmployee(false);
    }
  };

  const handleUpdateEmployee = async () => {
    if (!editEmployee) return;
    setSettingsError('');
    setSettingsSuccess('');
    setSavingEmployee(true);
    try {
      const payload = {
        ...editEmployee,
        allowedColumns: getOwnAllowedColumns(editEmployee, employeeColumnOptionsByKey),
      };
      const res = await apiFetch(`/api/employees/${editEmployee._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setSettingsError(await getErrorMessage(res, 'Не удалось обновить сотрудника.'));
        return;
      }
      resetEmployeeForm();
      setEmployeeModalMode('');
      await fetchEmployees();
      setSettingsSuccess('Данные сотрудника обновлены.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось обновить сотрудника.'));
    } finally {
      setSavingEmployee(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch(`/api/employees/${id}`, { method: 'DELETE' });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        setSettingsError(toUserErrorMessage(data?.message, 'Не удалось удалить сотрудника.'));
        return false;
      }
      if (editEmployee?._id === id) {
        resetEmployeeForm();
      }
      await fetchEmployees();
      setTelegramStageNotificationDraftIds((current) => (Array.isArray(current) ? current : []).filter((item) => item !== id));
      setTelegramNotificationDraftIds((current) => current.filter((item) => item !== id));
      setAppSettings((current) => ({
        ...current,
        telegramStageNotificationEmployeeIds: (
          Array.isArray(current.telegramStageNotificationEmployeeIds)
            ? current.telegramStageNotificationEmployeeIds
            : []
        ).filter((item) => item !== id),
        telegramRequestNotificationEmployeeIds: (
          Array.isArray(current.telegramRequestNotificationEmployeeIds)
            ? current.telegramRequestNotificationEmployeeIds
            : []
        ).filter((item) => item !== id),
        stageOwnershipBypassEmployeeIds: (
          Array.isArray(current.stageOwnershipBypassEmployeeIds)
            ? current.stageOwnershipBypassEmployeeIds
            : []
        ).filter((item) => item !== id),
      }));
      setSettingsSuccess(data?.warning || data?.message || 'Сотрудник удален.');
      return true;
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось удалить сотрудника.'));
      return false;
    }
  };

  // Settings
  const handleAddStep = async () => {
    if (!selectedStepsRole || !newStep.stepName || !newStep.description) return;
    setSettingsError('');
    setSettingsSuccess('');
    setSavingStep(true);
    try {
      const res = await apiFetch('/api/processSteps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newStep, role: selectedStepsRole }),
      });
      if (!res.ok) {
        setSettingsError(await getErrorMessage(res, 'Не удалось добавить этап.'));
        return;
      }
      setNewStep({ stepName: '', description: '', order: filteredSteps.length + 1 });
      setStepModalMode('');
      await fetchSteps();
      setSettingsSuccess('Этап добавлен.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось добавить этап.'));
    } finally {
      setSavingStep(false);
    }
  };

  const handleUpdateStep = async () => {
    if (!editStep) return;
    setSettingsError('');
    setSettingsSuccess('');
    setSavingStep(true);
    try {
      const res = await apiFetch(`/api/processSteps/${editStep._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stepName: editStep.stepName, description: editStep.description, order: editStep.order }),
      });
      if (!res.ok) {
        setSettingsError(await getErrorMessage(res, 'Не удалось обновить этап.'));
        return;
      }
      setEditStep(null);
      setStepModalMode('');
      await fetchSteps();
      setSettingsSuccess('Этап обновлен.');
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось обновить этап.'));
    } finally {
      setSavingStep(false);
    }
  };

  const handleDeleteStep = async (id) => {
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await apiFetch(`/api/processSteps/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        setSettingsError(await getErrorMessage(res, 'Не удалось удалить этап.'));
        return false;
      }
      await fetchSteps();
      setSettingsSuccess('Этап удален.');
      return true;
    } catch (error) {
      setSettingsError(toUserErrorMessage(error, 'Не удалось удалить этап.'));
      return false;
    }
  };

  const handleSaveLegendColors = async () => {
    if (!legendEditForm) return;
    setSettingsError('');
    setSettingsSuccess('');
    setSavingLegendColors(true);
    try {
      const normalizedHex = legendEditForm.useTableBackground
        ? ''
        : String(legendEditForm.hex || '').trim().toUpperCase();
      if (!legendEditForm.useTableBackground && !HEX_COLOR_PATTERN.test(normalizedHex)) {
        throw new Error(`Цвет ячейки "${legendEditForm.label || 'Без названия'}" должен быть в формате #RRGGBB.`);
      }
      const targetLegendKey = String(
        orderStageLegendConfig.secondaryHeaders?.[legendEditForm.headerIndex]?.legendKey
        || legendEditForm.legendKey
        || ''
      ).trim();

      const normalizedStages = (orderStageLegendConfig.stages || []).map((stage) => {
        const baseHex = String(stage.hex || stage.defaultHex || '').trim().toUpperCase() || '#FFFFFF';
        const syncedHex = targetLegendKey && targetLegendKey === String(stage.key || '').trim() && !legendEditForm.useTableBackground
          ? normalizedHex
          : baseHex;
        const defaultHexOut = targetLegendKey && targetLegendKey === String(stage.key || '').trim() && !legendEditForm.useTableBackground
          ? normalizedHex
          : String(stage.defaultHex || baseHex || '').trim().toUpperCase() || '#FFFFFF';
        return {
          key: stage.key,
          label: String(stage.label || '').trim(),
          description: String(stage.description || '').trim(),
          storeName: stage.storeName,
          defaultHex: defaultHexOut,
          hex: syncedHex || defaultHexOut,
        };
      });

      const configPayload = {
        primaryHeaders: (orderStageLegendConfig.primaryHeaders || []).map((label) => String(label ?? '')),
        stages: normalizedStages,
        secondaryHeaders: (orderStageLegendConfig.secondaryHeaders || []).map((item, index) => ({
          label: index === legendEditForm.headerIndex ? String(legendEditForm.label || '').trim() : item.label,
          legendKey: item.legendKey,
          hex: index === legendEditForm.headerIndex
            ? (legendEditForm.useTableBackground ? '' : normalizedHex)
            : (item.useTableBackground ? '' : String(item.hex || '').trim()),
        })),
      };

      const configRes = await apiFetch('/api/order-stage-legend-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configPayload),
      });
      if (!configRes.ok) {
        const errorData = await parseJsonSafely(configRes);
        const detailedMessage = buildLegendSaveErrorMessage({
          summary: 'Не удалось сохранить конфигурацию этапов.',
          step: 'PUT /api/order-stage-legend-config',
          status: configRes.status,
          statusText: configRes.statusText,
          serverMessage: errorData?.details || errorData?.message || '',
          selectedStageLabel: legendEditForm.stageLabel || '',
          selectedHeaderLabel: legendEditForm.label || '',
          stagesCount: normalizedStages.length,
          headersCount: configPayload.secondaryHeaders.length,
        });
        const error = new Error(detailedMessage);
        error.legendDebugMessage = detailedMessage;
        throw error;
      }

      await fetchOrderStageLegendConfig();
      setSettingsSuccess('Название и цвет ячейки обновлены.');
      closeLegendColorModal();
    } catch (error) {
      const detailedMessage = error?.legendDebugMessage || buildLegendSaveErrorMessage({
        summary: 'Не удалось сохранить настройки этапов.',
        step: 'Локальная подготовка данных',
        error,
        selectedStageLabel: legendEditForm?.stageLabel || '',
        selectedHeaderLabel: legendEditForm?.label || '',
        stagesCount: orderStageLegendConfig.stages?.length,
        headersCount: orderStageLegendConfig.secondaryHeaders?.length,
      });
      console.error('Legend save failed:', {
        error,
        selectedHeader: legendEditForm,
      });
      setSettingsError(detailedMessage);
    } finally {
      setSavingLegendColors(false);
    }
  };

  const settingsCrudModals = (
    <>
      <EmployeeModal
        mode={employeeModalMode}
        employee={employeeModalMode === 'edit' ? editEmployee : null}
        employeeForm={employeeForm}
        setEmployeeForm={setEmployeeForm}
        onAdd={handleAddEmployee}
        onUpdate={handleUpdateEmployee}
        onClose={closeEmployeeModal}
        saving={savingEmployee}
        columnOptions={employeeColumnOptions}
        sessionStatus={employeeSessionStatus}
        refreshingSession={employeeSessionRefreshing}
        onRefreshSession={handleRefreshEmployeeSessionToken}
        lastRefreshResult={employeeSessionLastRefresh}
        menuButtonCheckLoading={employeeSessionMenuButtonCheckLoading}
        onCheckMenuButton={handleCheckEmployeeMenuButton}
        lastMenuButtonCheck={employeeSessionLastMenuButtonCheck}
      />

      {stageManagerRoleKey ? (
        <div className="modal-overlay" onClick={savingStep ? undefined : closeStageManager}>
          <div className="modal-window modal-window-xl" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Этапы роли {getRoleLabel(stageManagerRoleKey)}</div>
                <div className="modal-subtitle">
                  Управляйте этапами выбранной роли прямо из действий.
                </div>
              </div>
              <button className="btn btn-small modal-close-btn" onClick={closeStageManager} disabled={savingStep}>
                ✕
              </button>
            </div>

            <SettingsFeedback error={settingsError} success={settingsSuccess} />
            <SettingsActions>
              <button className="btn btn-success" onClick={openCreateStepModal} disabled={!selectedStepsRole}>
                Добавить этап
              </button>
              <button className="btn" onClick={fetchSteps}>
                Обновить этапы
              </button>
            </SettingsActions>

            <div className="table-scroll desktop-table-only">
              <table>
                <thead><tr><th>№</th><th>Название этапа</th><th>Описание</th><th>Действия</th></tr></thead>
                <tbody>
                  {filteredSteps.map(step => (
                    <tr key={step._id}>
                      <td>{step.order}</td>
                      <td>{step.stepName}</td>
                      <td>{step.description}</td>
                      <td>
                        <button className="btn btn-primary" style={{ marginRight: 6 }} onClick={() => openEditStepModal(step)}>✎</button>
                        <button className="btn btn-danger" onClick={() => requestDeleteStep(step)}>✕</button>
                      </td>
                    </tr>
                  ))}
                  {filteredSteps.length === 0 && <tr><td colSpan={4} className="empty-cell">Нет этапов для выбранной роли</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mobile-card-list">
              {filteredSteps.map(step => (
                <div key={step._id} className="mobile-settings-card">
                  <div className="mobile-settings-card-header">
                    <div>
                      <div className="mobile-order-card-title">{step.stepName}</div>
                      <div className="mobile-order-card-subtitle">Порядок: {step.order}</div>
                    </div>
                  </div>
                  <div className="mobile-settings-card-note">
                    {step.description || 'Без описания'}
                  </div>
                  <div className="mobile-settings-card-actions">
                    <button className="btn btn-primary" onClick={() => openEditStepModal(step)}>Редактировать</button>
                    <button className="btn btn-danger" onClick={() => requestDeleteStep(step)}>Удалить</button>
                  </div>
                </div>
              ))}
              {filteredSteps.length === 0 && <div className="mobile-empty-state">Нет этапов для выбранной роли</div>}
            </div>
          </div>
        </div>
      ) : null}

      <StepModal
        mode={stepModalMode}
        editStep={editStep}
        newStep={newStep}
        setEditStep={setEditStep}
        setNewStep={setNewStep}
        onAdd={handleAddStep}
        onUpdate={handleUpdateStep}
        onClose={closeStepModal}
        saving={savingStep}
      />

      <Modal
        open={showLegendColorModal}
        onClose={closeLegendColorModal}
        closeDisabled={savingLegendColors}
        size="md"
        className="stage-legend-simple-modal"
      >
        <ModalHeader
          title="Редактирование ячейки"
          subtitle={legendEditForm ? `Текущий этап: ${legendEditForm.stageLabel}` : 'Измените название и цвет выбранной ячейки второй строки.'}
          onClose={closeLegendColorModal}
          closeDisabled={savingLegendColors}
        />
        {legendEditForm ? (
          <>
            <div className="stage-legend-simple-preview-row">
              <span
                className={`orders-stage-legend-swatch ${legendEditForm.useTableBackground ? 'orders-stage-legend-swatch-table' : ''}`}
                style={{ background: legendEditForm.useTableBackground ? '#0C1A2A' : legendEditForm.hex }}
              />
              <div className="stage-legend-simple-preview-text">
                <strong>{legendEditForm.label || 'Без названия'}</strong>
                <span>{legendEditForm.stageLabel}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Название ячейки</label>
              <input
                value={legendEditForm.label}
                onChange={(event) => setLegendEditForm((current) => current ? { ...current, label: event.target.value } : current)}
                placeholder="Введите название из второй строки"
                disabled={savingLegendColors}
              />
            </div>
            <div className="form-group">
              <label>Цвет ячейки</label>
              {legendEditForm.useTableBackground ? (
                <input value="Цвет таблицы" disabled />
              ) : (
                <div className="stage-legend-simple-color-row">
                  <input
                    type="color"
                    value={legendEditForm.hex || '#FFFFFF'}
                    onChange={(event) => setLegendEditForm((current) => current ? { ...current, hex: event.target.value } : current)}
                    disabled={savingLegendColors}
                  />
                  <input
                    value={legendEditForm.hex || '#FFFFFF'}
                    onChange={(event) => setLegendEditForm((current) => current ? { ...current, hex: event.target.value } : current)}
                    placeholder="#RRGGBB"
                    disabled={savingLegendColors}
                  />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <Button variant="success" onClick={handleSaveLegendColors} disabled={savingLegendColors}>
                {savingLegendColors ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button onClick={closeLegendColorModal} disabled={savingLegendColors}>Отмена</Button>
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        open={showTelegramNotificationModal}
        onClose={closeTelegramNotificationModal}
        closeDisabled={savingTelegramNotifications}
        size="lg"
      >
        <ModalHeader
          title="Уведомления в ТГ"
          subtitle="Разделите получателей по типам уведомлений: этапы/статусы заказов и заявки снабжения."
          onClose={closeTelegramNotificationModal}
          closeDisabled={savingTelegramNotifications}
        />
        <div className="tabs" style={{ marginBottom: 12 }}>
          <button
            key="stages"
            type="button"
            className={`tab ${telegramNotificationActiveTab === 'stages' ? 'tab-active' : ''}`}
            onClick={() => setTelegramNotificationActiveTab('stages')}
          >
            📋 Статусы (этапы заказов)
          </button>
          <button
            key="requests"
            type="button"
            className={`tab ${telegramNotificationActiveTab === 'requests' ? 'tab-active' : ''}`}
            onClick={() => setTelegramNotificationActiveTab('requests')}
          >
            📦 Заявки (снабжение)
          </button>
        </div>
        <div className="text-small text-subtle" style={{ marginBottom: 10 }}>
          {telegramNotificationActiveTab === 'stages'
            ? 'Эти сотрудники будут получать уведомления об изменениях этапов и статусов заказов (от основного бота).'
            : 'Эти сотрудники будут получать уведомления о новых заявках на закупку расходников, запчастей и деталей (от бота снабжения).'}
        </div>
        <div className="telegram-notification-list">
          {employees.length > 0 ? employees.map((employee) => {
            const employeeId = String(employee._id || '').trim();
            const isTelegramReady = telegramReadyEmployeeIds.has(employeeId);
            const draftList = telegramNotificationActiveTab === 'stages' ? telegramStageNotificationDraftIds : telegramNotificationDraftIds;
            const checked = draftList.includes(employeeId);
            return (
              <label key={employeeId} className={`telegram-notification-row ${!isTelegramReady ? 'telegram-notification-row-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!isTelegramReady || savingTelegramNotifications}
                  onChange={(event) => toggleTelegramNotificationEmployee(employeeId, event.target.checked)}
                />
                <div className="telegram-notification-row-body">
                  <div className="telegram-notification-row-title">
                    <strong>{employee.fullName || 'Без имени'}</strong>
                    <span>{getRoleLabel(employee.role) || 'Без должности'}</span>
                  </div>
                  <div className="telegram-notification-row-meta">
                    {isTelegramReady
                      ? getEmployeeTelegramSummary(employee)
                      : 'Сотрудник ещё не привязан к Telegram-боту, поэтому уведомления ему недоступны.'}
                  </div>
                </div>
              </label>
            );
          }) : (
            <div className="mobile-empty-state">Сотрудники пока не добавлены.</div>
          )}
        </div>
        <div className="modal-actions">
          <Button variant="success" onClick={saveTelegramNotificationSettings} disabled={savingTelegramNotifications}>
            {savingTelegramNotifications ? 'Сохранение...' : 'Сохранить оба списка'}
          </Button>
          <Button onClick={closeTelegramNotificationModal} disabled={savingTelegramNotifications}>Отмена</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        onConfirm={handleConfirmDelete}
        onCancel={() => !confirmLoading && setConfirmAction(null)}
        loading={confirmLoading}
      />

      <Modal
        open={Boolean(modalErrorMessage)}
        onClose={() => setModalErrorMessage('')}
        size="sm"
        className="modal-window-top"
      >
        <ModalHeader
          title="Ошибка"
          subtitle="Исправьте данные и повторите действие."
          onClose={() => setModalErrorMessage('')}
        />
        <div className="settings-alert settings-alert-error" style={{ whiteSpace: 'pre-wrap', marginBottom: 16 }}>
          {modalErrorMessage}
        </div>
        <div className="modal-actions">
          <Button onClick={() => setModalErrorMessage('')}>Закрыть</Button>
        </div>
      </Modal>
    </>
  );

  if (activeRole === 'general') {
    return (
      <div>
        <SettingsHeader title="⚙️ Настройки — Общие параметры" onBack={() => navigate('/orders')} activeRole={activeRole} onTabChange={handleSettingsTabChange} tabs={[]} />

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="tabs">
            <button className={`tab ${generalSubTab === 'updates' ? 'tab-active' : ''}`} onClick={() => handleGeneralSubTabChange('updates')} type="button">
              🔄 Обновления
            </button>
            <button className={`tab ${generalSubTab === 'bot' ? 'tab-active' : ''}`} onClick={() => handleGeneralSubTabChange('bot')} type="button">
              🤖 Телеграм бот
            </button>
            <button className={`tab ${generalSubTab === 'access' ? 'tab-active' : ''}`} onClick={() => handleGeneralSubTabChange('access')} type="button">
              🔑 Доступ
            </button>
          </div>
        </div>

        <input
          ref={backupImportInputRef}
          type="file"
          accept="application/json,.json"
          onChange={handleBackupImport}
          style={{ display: 'none' }}
        />

        {generalSubTab === 'updates' && (
          <UpdatesOverview
            updateStatus={updateStatus}
            installJob={installJob}
            updateMessage={updateMessage}
            updateError={updateError}
            checkingUpdates={checkingUpdates}
            installingUpdates={installingUpdates}
            appSettings={appSettings}
            onSettingsChange={setAppSettings}
            onRefresh={fetchUpdateStatus}
            onInstall={installUpdates}
            onSaveUpdateSettings={saveUpdateSettings}
            savingUpdateSettings={savingUpdateSettings}
          />
        )}

        {generalSubTab === 'bot' && (
          <div className="card">
            <p>Здесь можно настроить адрес проекта для QR-кодов и токены Telegram-ботов.</p>
            <SettingsFeedback error={settingsError} success={settingsSuccess} />

            <div className="form-group">
              <label className="helper-label">
                Публичный адрес проекта
                <HelpTooltip text="Укажите внешний HTTPS-адрес сайта. Он используется в QR-кодах и для Telegram webhook, поэтому адрес должен открываться из интернета." />
              </label>
              <input
                value={appSettings.publicBaseUrl}
                onChange={e => setAppSettings({ ...appSettings, publicBaseUrl: e.target.value })}
                placeholder="Например: https://factory.example.com"
              />
            </div>

            <SectionHeader
              title="🤖 Основной Telegram-бот"
              description="Используется для уведомлений об этапах заказов, авторизации мастеров и других рабочих процессов."
            />

            <div className="form-group">
              <label className="helper-label">
                Токен основного Telegram-бота
                <HelpTooltip text="Вставьте bot token, полученный у BotFather, в формате 123456789:AA.... Этот токен нужен для проверки бота, установки webhook и отправки ответов сотрудникам." />
              </label>
              <input
                type="password"
                value={appSettings.telegramBotToken}
                onChange={e => setAppSettings({ ...appSettings, telegramBotToken: e.target.value })}
                placeholder="Например: 123456789:AA..."
              />
            </div>

            <SettingsActions>
              <button className="btn btn-success" onClick={saveAppSettings} disabled={savingAppSettings}>
                {savingAppSettings ? 'Сохранение...' : 'Сохранить настройки'}
              </button>
              <button className="btn btn-primary" onClick={checkTelegramBot} disabled={checkingTelegramBot || savingAppSettings}>
                {checkingTelegramBot ? 'Проверка...' : 'Проверить бота'}
              </button>
              <button className="btn" onClick={setupTelegramWebhook} disabled={settingTelegramWebhook || savingAppSettings}>
                {settingTelegramWebhook ? 'Установка...' : 'Установить webhook'}
              </button>
              <button
                className="btn btn-info"
                onClick={refreshAuthorizedTelegramMenuButtons}
                disabled={refreshingAuthorizedMenuButtons || settingTelegramWebhook || savingAppSettings}
              >
                {refreshingAuthorizedMenuButtons ? 'Обновление...' : '🔄 Обновить кнопки ТГ'}
              </button>
              <button className="btn btn-secondary" onClick={() => fetchTelegramLogs({ openModal: true })} disabled={telegramLogsLoading}>
                {telegramLogsLoading ? 'Загрузка логов...' : 'Логи ТГ бота'}
              </button>
            </SettingsActions>

            {telegramCheckResult && (
              <div className="panel-info">
                <div className="panel-info-title">Основной Telegram-бот подключен</div>
                <div className="panel-info-grid">
                  <div><strong>Бот:</strong> {telegramCheckResult?.bot?.firstName || '—'}</div>
                  <div><strong>Username:</strong> {telegramCheckResult?.bot?.username ? `@${telegramCheckResult.bot.username}` : '—'}</div>
                  <div><strong>Webhook:</strong> {telegramCheckResult?.webhook?.url || 'не настроен'}</div>
                  <div><strong>Ожидает обновлений:</strong> {telegramCheckResult?.webhook?.pendingUpdateCount ?? 0}</div>
                </div>
                <div className="panel-info-text">
                  Для авторизации сотрудников webhook должен указывать на адрес:
                  <div className="panel-info-code text-mono">
                    {telegramCheckResult.recommendedWebhookUrl}
                  </div>
                </div>
                {telegramCheckResult?.webhook?.lastErrorMessage && (
                  <div className="mt-10 text-danger text-small-13">
                    Последняя ошибка webhook: {telegramCheckResult.webhook.lastErrorMessage}
                  </div>
                )}
              </div>
            )}

            <SectionHeader
              title="📦 Telegram-бот отдела снабжения"
              description="Отдельный бот для заявок на закупку расходников, запчастей, деталей и прочих нужд снабжения."
            />

            <div className="form-group">
              <label className="helper-label">
                Токен Telegram-бота снабжения
                <HelpTooltip text="Вставьте bot token отдельного бота для отдела снабжения. На этот токен будут приходить уведомления о новых заявках на расходники и запчасти." />
              </label>
              <input
                type="password"
                value={appSettings.telegramSupplyBotToken}
                onChange={e => setAppSettings({ ...appSettings, telegramSupplyBotToken: e.target.value })}
                placeholder="Например: 987654321:BB..."
              />
            </div>

            <SettingsActions>
              <button className="btn btn-primary" onClick={checkTelegramSupplyBot} disabled={checkingTelegramSupplyBot || savingAppSettings}>
                {checkingTelegramSupplyBot ? 'Проверка...' : 'Проверить бота снабжения'}
              </button>
              <button className="btn" onClick={setupTelegramSupplyWebhook} disabled={settingTelegramSupplyWebhook || savingAppSettings}>
                {settingTelegramSupplyWebhook ? 'Установка...' : 'Установить webhook снабжения'}
              </button>
              <button
                className="btn btn-info"
                onClick={refreshAuthorizedSupplyTelegramMenuButtons}
                disabled={refreshingAuthorizedSupplyMenuButtons || settingTelegramSupplyWebhook || savingAppSettings}
              >
                {refreshingAuthorizedSupplyMenuButtons ? 'Обновление...' : '🔄 Обновить кнопки снабжения'}
              </button>
            </SettingsActions>

            {telegramSupplyCheckResult && (
              <div className="panel-info">
                <div className="panel-info-title">Telegram-бот снабжения подключен</div>
                <div className="panel-info-grid">
                  <div><strong>Бот:</strong> {telegramSupplyCheckResult?.bot?.firstName || '—'}</div>
                  <div><strong>Username:</strong> {telegramSupplyCheckResult?.bot?.username ? `@${telegramSupplyCheckResult.bot.username}` : '—'}</div>
                  <div><strong>Webhook:</strong> {telegramSupplyCheckResult?.webhook?.url || 'не настроен'}</div>
                  <div><strong>Ожидает обновлений:</strong> {telegramSupplyCheckResult?.webhook?.pendingUpdateCount ?? 0}</div>
                </div>
                <div className="panel-info-text">
                  Webhook бота снабжения должен указывать на адрес:
                  <div className="panel-info-code text-mono">
                    {telegramSupplyCheckResult.recommendedWebhookUrl}
                  </div>
                </div>
                {telegramSupplyCheckResult?.webhook?.lastErrorMessage && (
                  <div className="mt-10 text-danger text-small-13">
                    Последняя ошибка webhook: {telegramSupplyCheckResult.webhook.lastErrorMessage}
                  </div>
                )}
              </div>
            )}

            <SectionHeader
              title="💬 Супергруппа Telegram (общая)"
              description="Используется основным ботом для автоматического создания topic-групп к каждому новому заказу."
            />

            <div className="form-group">
              <label className="helper-label">
                ID супергруппы Telegram
                <HelpTooltip text="Укажите chat id супергруппы с темами, например -1001234567890. В эту группу бот будет автоматически создавать topic для каждого нового заказа." />
              </label>
              <input
                value={appSettings.telegramSupergroupChatId}
                onChange={e => setAppSettings({ ...appSettings, telegramSupergroupChatId: e.target.value })}
                placeholder="Например: -1001234567890"
              />
            </div>

            <div className="form-group">
              <label className="settings-switch">
                <div className="settings-switch-copy">
                  <span className="settings-switch-title">Мост заказчик ↔ супергруппа Telegram</span>
                  <span className="settings-switch-subtitle">
                    {appSettings.telegramSupergroupEnabled ? 'On' : 'Off'}
                  </span>
                </div>
                <span className={`settings-switch-control ${appSettings.telegramSupergroupEnabled ? 'settings-switch-control-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={Boolean(appSettings.telegramSupergroupEnabled)}
                    onChange={e => setAppSettings({ ...appSettings, telegramSupergroupEnabled: e.target.checked })}
                  />
                  <span className="settings-switch-track" aria-hidden="true">
                    <span className="settings-switch-thumb" />
                  </span>
                </span>
              </label>
            </div>

          </div>
        )}

        {generalSubTab === 'access' && (
          <div className="card">
            <p>Управление доступом к настройкам, журнал действий и резервное копирование данных.</p>
            <SettingsFeedback error={settingsError} success={settingsSuccess} />
            <SettingsActions>
              <button className="btn btn-secondary" onClick={() => fetchActivityLogs({ openModal: true })} disabled={activityLogsLoading}>
                {activityLogsLoading ? 'Загрузка журнала...' : 'Журнал действий'}
              </button>
              <button className="btn btn-secondary" onClick={exportBackup} disabled={exportingBackup}>
                {exportingBackup ? 'Экспорт...' : 'Экспорт данных'}
              </button>
              <button className="btn btn-secondary" onClick={openBackupImportPicker} disabled={importingBackup}>
                {importingBackup ? 'Импорт...' : 'Импорт данных'}
              </button>
            </SettingsActions>

            <AdminTokenControls />
          </div>
        )}

        {showTelegramLogs && (
          <div className="modal-overlay" onClick={() => setShowTelegramLogs(false)}>
            <div className="modal-window modal-window-xl" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">Логи ТГ бота</div>
                  <div className="modal-subtitle">
                    Здесь сохраняются диагностические события Telegram Web App и Telegram-заказов.
                  </div>
                </div>
                <button className="btn btn-small modal-close-btn" onClick={() => setShowTelegramLogs(false)}>
                  ✕
                </button>
              </div>

              <div className="modal-actions modal-actions-between">
                <div className="modal-actions-group">
                  <button className="btn btn-secondary" onClick={() => fetchTelegramLogs()} disabled={telegramLogsLoading}>
                    {telegramLogsLoading ? 'Обновление...' : 'Обновить'}
                  </button>
                  <button className="btn btn-danger" onClick={requestClearTelegramLogs} disabled={clearingTelegramLogs || confirmLoading}>
                    {clearingTelegramLogs ? 'Очистка...' : 'Очистить лог'}
                  </button>
                </div>
                <div className="text-small text-subtle">
                  Записей: {telegramLogs.length}
                </div>
              </div>

              {telegramLogs.length > 0 ? (
                <div className="telegram-log-list">
                  {telegramLogs.map((entry) => (
                    <pre key={entry.id || `${entry.createdAt}-${entry.event}`} className="service-details-console service-details-console-status telegram-log-entry">
                      {formatTelegramLogEntry(entry)}
                    </pre>
                  ))}
                </div>
              ) : (
                <div className="mobile-empty-state">
                  {telegramLogsLoading ? 'Загружаю логи ТГ бота...' : 'Логи пока пусты.'}
                </div>
              )}
            </div>
          </div>
        )}

        {showActivityLogs && (
          <div className="modal-overlay" onClick={() => setShowActivityLogs(false)}>
            <div className="modal-window modal-window-xl" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="modal-title">Журнал действий</div>
                  <div className="modal-subtitle">
                    Здесь сохраняются изменения по заказам, сотрудникам, настройкам и справочникам.
                  </div>
                </div>
                <button className="btn btn-small modal-close-btn" onClick={() => setShowActivityLogs(false)}>
                  ✕
                </button>
              </div>

              <div className="modal-actions modal-actions-between">
                <div className="modal-actions-group">
                  <button className="btn btn-secondary" onClick={() => fetchActivityLogs()} disabled={activityLogsLoading}>
                    {activityLogsLoading ? 'Обновление...' : 'Обновить'}
                  </button>
                  <button className="btn btn-danger" onClick={requestClearActivityLogs} disabled={clearingActivityLogs || confirmLoading}>
                    {clearingActivityLogs ? 'Очистка...' : 'Очистить журнал'}
                  </button>
                </div>
                <div className="text-small text-subtle">
                  Записей: {activityLogs.length}
                </div>
              </div>

              {activityLogs.length > 0 ? (
                <div className="telegram-log-list">
                  {activityLogs.map((entry) => (
                    <pre key={entry._id || `${entry.createdAt}-${entry.action}`} className="service-details-console service-details-console-status telegram-log-entry">
                      {formatActivityLogEntry(entry)}
                    </pre>
                  ))}
                </div>
              ) : (
                <div className="mobile-empty-state">
                  {activityLogsLoading ? 'Загружаю журнал действий...' : 'Журнал действий пока пуст.'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (activeRole === 'employees') {
    const getEmployeeBotKinds = (employee) => {
      if (Array.isArray(employee?.telegramBotKinds) && employee.telegramBotKinds.length) {
        return employee.telegramBotKinds.filter((k) => k === 'primary' || k === 'supply');
      }
      const single = String(employee?.telegramBotKind || 'primary').trim();
      return single === 'supply' ? ['supply'] : ['primary'];
    };
    const getEmployeeBotKindsLabel = (kinds) => {
      const hasPrimary = kinds.includes('primary');
      const hasSupply = kinds.includes('supply');
      if (hasPrimary && hasSupply) return 'Оба бота';
      if (hasSupply) return 'Бот снабжения';
      return 'Основной бот';
    };
    const getEmployeeBotKindsBadgeColor = (kinds) => {
      const hasPrimary = kinds.includes('primary');
      const hasSupply = kinds.includes('supply');
      if (hasPrimary && hasSupply) return { background: '#e8f7ee', border: '1px solid #b8e6c9', color: '#2b7947' };
      if (hasSupply) return { background: '#e9f1ff', border: '1px solid #c7d8f6', color: '#2c55a6' };
      return { background: '#eaf3fb', border: '1px solid #c6e0f5', color: '#1f4f7a' };
    };
    return (
      <div>
        <SettingsHeader title="⚙️ Настройки — Сотрудники" onBack={() => navigate('/orders')} activeRole={activeRole} onTabChange={handleSettingsTabChange} tabs={[]} />

          <div className="card" style={{ marginBottom: 20 }}>
            <p>Список сотрудников для входа в Telegram-бот и работы с заказами по ролям.</p>
            <SettingsHint>
              <div><strong>Как работать:</strong> нажмите "Добавить сотрудника", заполните ФИО, должность, закрепленные колонки и PIN-код.</div>
              <div><strong>Редактирование:</strong> используйте кнопку ✎ или "Редактировать" в карточке сотрудника.</div>
              <div><strong>Удаление:</strong> используйте кнопку ✕ или "Удалить", подтвердите действие, после чего привязанному сотруднику придет уведомление в Telegram.</div>
            </SettingsHint>
            <SettingsFeedback error={settingsError} success={settingsSuccess} />

            <SettingsActions>
              <button className="btn btn-success" onClick={openCreateEmployeeModal}>Добавить сотрудника</button>
              <button className="btn" onClick={openStageOwnershipModal}>⚙️ Управление этапами</button>
              <button className="btn" onClick={openTelegramNotificationModal}>Уведомления в ТГ</button>
            </SettingsActions>

            <div className="table-scroll desktop-table-only">
              <table>
                <thead><tr><th>ФИО</th><th>Должность</th><th>Колонки</th><th>Статус TG</th><th>Пользователь TG</th><th>Авторизован</th><th>PIN</th><th>Действия</th></tr></thead>
                <tbody>
                  {employees.map(employee => (
                    <tr key={employee._id}>
                      <td>
                        <div className="employee-name-cell">
                          <span>{employee.fullName}</span>
                          {telegramNotificationEmployeeIdsSet.has(String(employee._id || '').trim()) ? (
                            <span className="badge badge-success employee-telegram-notice-badge">Уведомления ТГ</span>
                          ) : null}
                        </div>
                      </td>
                      <td>{getRoleLabel(employee.role) || '—'}</td>
                      <td>{renderAllowedColumnsMarkers(employee, { compact: true, ownOnly: true, optionsByKey: employeeColumnOptionsByKey })}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span>{employee.telegramUserId ? 'Привязан' : 'Не привязан'}</span>
                          {(() => {
                            const kinds = getEmployeeBotKinds(employee);
                            const badgeStyle = getEmployeeBotKindsBadgeColor(kinds);
                            return (
                              <span
                                className="badge"
                                style={{
                                  ...badgeStyle,
                                  display: 'inline-flex',
                                  width: 'fit-content',
                                  fontSize: 11,
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  alignSelf: 'flex-start',
                                }}
                              >
                                {getEmployeeBotKindsLabel(kinds)}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td>{getEmployeeTelegramSummary(employee)}</td>
                      <td>{employee.telegramAuthorizedAt ? formatDateTimeDisplay(employee.telegramAuthorizedAt) : '—'}</td>
                      <td>{employee.pinCode || (employee.telegramUserId ? 'Использован' : '—')}</td>
                      <td>
                        <button className="btn btn-primary" style={{ marginRight: 6 }} onClick={() => openEditEmployeeModal(employee)}>✎</button>
                        <button className="btn btn-danger" onClick={() => requestDeleteEmployee(employee)}>✕</button>
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && <tr><td colSpan={8} className="empty-cell">Сотрудники пока не добавлены</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mobile-card-list">
              {employees.map(employee => (
                <div key={employee._id} className="mobile-settings-card">
                  <div className="mobile-settings-card-header">
                    <div>
                      <div className="mobile-order-card-title">{employee.fullName}</div>
                      {telegramNotificationEmployeeIdsSet.has(String(employee._id || '').trim()) ? (
                        <div style={{ marginTop: 8 }}>
                          <span className="badge badge-success employee-telegram-notice-badge">Уведомления ТГ</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="mobile-order-card-subtitle">{getRoleLabel(employee.role) || 'Должность не указана'}</div>
                  </div>
                  <div className="mobile-settings-card-meta">
                    <div>
                      <strong>Статус TG:</strong> {employee.telegramUserId ? 'Привязан' : 'Не привязан'}
                      {(() => {
                        const kinds = getEmployeeBotKinds(employee);
                        const badgeStyle = getEmployeeBotKindsBadgeColor(kinds);
                        return (
                          <span
                            className="badge"
                            style={{
                              ...badgeStyle,
                              display: 'inline-flex',
                              marginLeft: 8,
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 999,
                              fontWeight: 600,
                              verticalAlign: 'middle',
                            }}
                          >
                            {getEmployeeBotKindsLabel(kinds)}
                          </span>
                        );
                      })()}
                    </div>
                    <div><strong>Пользователь TG:</strong> {employee.telegramUserId ? employee.telegramUsername || 'без username' : '—'}</div>
                    <div><strong>Авторизован:</strong> {employee.telegramAuthorizedAt ? formatDateTimeDisplay(employee.telegramAuthorizedAt) : '—'}</div>
                    <div><strong>PIN:</strong> {employee.pinCode || (employee.telegramUserId ? 'Использован' : '—')}</div>
                  </div>
                  <div className="mobile-settings-card-note">
                    <strong>Колонки:</strong>
                    <div style={{ marginTop: 8 }}>{renderAllowedColumnsMarkers(employee, { compact: true, ownOnly: true, optionsByKey: employeeColumnOptionsByKey })}</div>
                  </div>
                  {employee.telegramUserId ? (
                    <div className="mobile-settings-card-note">
                      {getEmployeeTelegramSummary(employee)}
                    </div>
                  ) : null}
                  <div className="mobile-settings-card-actions">
                    <button className="btn btn-primary" onClick={() => openEditEmployeeModal(employee)}>Редактировать</button>
                    <button className="btn btn-danger" onClick={() => requestDeleteEmployee(employee)}>Удалить</button>
                  </div>
                </div>
              ))}
              {employees.length === 0 && <div className="mobile-empty-state">Сотрудники пока не добавлены</div>}
            </div>
          </div>

          {showStageOwnershipModal && (
            <div className="modal-overlay" onClick={closeStageOwnershipModal}>
              <div className="modal-window" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 520 }}>
                <div className="modal-header">
                  <div>
                    <div className="modal-title">Управление этапами</div>
                    <div className="modal-subtitle">
                      Глобальные правила отмены этапов. Зависит от того, кто принял этап.
                    </div>
                  </div>
                  <button className="btn btn-small modal-close-btn" onClick={closeStageOwnershipModal} disabled={savingStageOwnership}>
                    ✕
                  </button>
                </div>

                <div className="form-group">
                  <label className="settings-switch">
                    <div className="settings-switch-copy">
                      <span className="settings-switch-title">Управление этапами</span>
                      <span className="settings-switch-subtitle">
                        {Boolean(stageOwnershipEnabledDraft)
                          ? 'Только тот, кто принял этап, может его отменить'
                          : 'Все сотрудники с доступом к этапу могут его принимать и отменять'}
                      </span>
                    </div>
                    <span className={`settings-switch-control ${Boolean(stageOwnershipEnabledDraft) ? 'settings-switch-control-on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(stageOwnershipEnabledDraft)}
                        onChange={(e) => setStageOwnershipEnabledDraft(e.target.checked)}
                      />
                      <span className="settings-switch-track" aria-hidden="true">
                        <span className="settings-switch-thumb" />
                      </span>
                    </span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="helper-label">
                    Исключения по сотрудникам
                    <HelpTooltip text="Эти сотрудники смогут сбрасывать закраску, даже если этап был принят другим исполнителем. Администратор с полным доступом тоже всегда может обойти запрет." />
                  </label>
                  <div className="stage-ownership-exceptions-list">
                    {sortedEmployees.map((employee) => {
                      const employeeId = String(employee?._id || '').trim();
                      const checked = stageOwnershipBypassDraftIds.includes(employeeId);
                      return (
                        <label key={employeeId} className="stage-ownership-exception-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => {
                              const isChecked = event.target.checked;
                              setStageOwnershipBypassDraftIds((current) => {
                                const next = new Set(normalizeEmployeeIds(current));
                                if (isChecked) next.add(employeeId);
                                else next.delete(employeeId);
                                return Array.from(next);
                              });
                            }}
                            disabled={savingStageOwnership}
                          />
                          <span className="stage-ownership-exception-copy">
                            <span className="stage-ownership-exception-name">
                              {employee.fullName || 'Без имени'}
                            </span>
                            <span className="stage-ownership-exception-role">
                              {employee.role ? getRoleLabel(employee.role) : 'Без роли'}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                    {sortedEmployees.length === 0 ? (
                      <div className="text-small text-subtle">Сотрудников пока нет.</div>
                    ) : null}
                  </div>
                  <div className="text-small text-subtle" style={{ marginTop: 8 }}>
                    Отметь сотрудников, которым разрешён обход запрета на сброс чужой закраски.
                  </div>
                  <div className="text-small text-subtle" style={{ marginTop: 8 }}>
                    {stageOwnershipBypassDraftIds.length > 0
                      ? `Будет сохранено: ${sortedEmployees.filter((employee) => stageOwnershipBypassDraftIds.includes(String(employee?._id || '').trim())).map((employee) => employee.fullName || 'Без имени').join(', ')}`
                      : 'Исключений пока нет.'}
                  </div>
                </div>

                <div className="modal-actions">
                  <div />
                  <button className="btn" onClick={closeStageOwnershipModal} disabled={savingStageOwnership}>
                    Закрыть
                  </button>
                  <button className="btn btn-success" onClick={saveStageOwnershipSettings} disabled={savingStageOwnership}>
                    {savingStageOwnership ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {settingsCrudModals}
      </div>
    );
  }

  if (activeRole === 'colors') {
    return (
      <div>
        <SettingsHeader title="⚙️ Настройки — Этапы производства" onBack={() => navigate('/orders')} activeRole={activeRole} onTabChange={handleSettingsTabChange} tabs={[]} />

          <div className="card">
            <p>Здесь настраиваются этапы производства, цвета ячеек второй строки и привязка колонок для единой таблицы заказов.</p>
            <SettingsFeedback error={settingsError} success={settingsSuccess} />
            <div className="orders-stage-legend">
              <div className="orders-stage-legend-header">
                <div>
                  <div className="orders-stage-legend-title">Этапы производства</div>
                  <div className="orders-stage-legend-subtitle">Цвета ячеек второй строки, названия этапов и привязка колонок ниже применяются ко всей логике таблицы заказов.</div>
                </div>
              </div>
              <div className="orders-stage-legend-grid">
                {productionHeaderPreviewItems.map((item, index) => (
                  <div key={`${item.label}-${index}`} className="orders-stage-legend-item">
                    <span
                      className={`orders-stage-legend-swatch ${item.useTableBackground ? 'orders-stage-legend-swatch-table' : ''}`}
                      style={{ background: item.previewHex }}
                    />
                    <div className="orders-stage-legend-content">
                      <div className="orders-stage-legend-item-title">{item.previewTitle}</div>
                    </div>
                    <div className="orders-stage-legend-editor">
                      <div className="orders-stage-legend-hex">{item.useTableBackground ? 'Цвет таблицы' : item.hex}</div>
                      <button
                        type="button"
                        className="btn btn-small btn-secondary"
                        onClick={() => openLegendColorModal(item.headerIndex)}
                        title={`Редактировать "${item.label || item.previewTitle}"`}
                        aria-label={`Редактировать "${item.label || item.previewTitle}"`}
                      >
                        ✎
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {settingsCrudModals}
      </div>
    );
  }

  return null;
}

export default Admin;
